import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Lock, Unlock, Save } from 'lucide-react'
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DateInput } from '@/components/ui/date-input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  getDeveloperWorkspace,
  forceSetSubscription,
  unlockAllResources,
} from '@/services/api/developer.service'
import { SubscriptionStatus } from '@/types/enums'
import { formatDate } from '@/utils/date'


const DEV_PLANS = [
  { code: 'FREE', name: 'Free' },
  { code: 'STARTER', name: 'Starter' },
  { code: 'PRO', name: 'Pro' },
  { code: 'ENTERPRISE', name: 'Enterprise' },
]

export function WorkspaceSubscriptionPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: workspace, isLoading } = useQuery({
    queryKey: ['developer', 'workspace', workspaceId],
    queryFn: () => getDeveloperWorkspace(workspaceId!),
    enabled: !!workspaceId,
  })

  const [planCode, setPlanCode] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [confirmWorkspaceName, setConfirmWorkspaceName] = useState('')

  const setSubscriptionMutation = useMutation({
    mutationFn: () =>
      forceSetSubscription(workspaceId!, {
        templateCode: planCode,
        startDate,
        endDate,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'workspace', workspaceId] })
      toast.success('Subscription updated')
    },
    onError: () => {
      toast.error('Failed to update subscription')
    },
  })

  const unlockAllMutation = useMutation({
    mutationFn: () => unlockAllResources(workspaceId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'workspace', workspaceId] })
      toast.success('All resources unlocked')
    },
    onError: () => {
      toast.error('Failed to unlock resources')
    },
  })

  const getStatusBadge = (status: string | undefined) => {
    if (!status) return <Badge variant="outline">No Subscription</Badge>
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return <Badge variant="success">Active</Badge>
      case SubscriptionStatus.TRIALING:
        return <Badge variant="warning">Trial</Badge>
      case SubscriptionStatus.EXPIRED:
        return <Badge variant="destructive">Expired</Badge>
      case SubscriptionStatus.CANCELLED:
        return <Badge variant="secondary">Cancelled</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!workspace) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h2 className="text-lg font-medium">Workspace not found</h2>
          <Button variant="link" onClick={() => navigate('/developer')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/developer')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>
        <h1 className="mt-2 text-3xl font-bold">{workspace.workspaceName}</h1>
        <p className="mt-1 text-neutral-500">{workspace.workspaceId}</p>
      </div>

      {/* Current Subscription */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription Status</CardTitle>
          <CardDescription>
            Current subscription details for this workspace
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <p className="text-sm text-neutral-500">Plan</p>
              <p className="text-lg font-medium">
                {workspace.subscription?.planName || 'None'}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-500">Status</p>
              <div className="mt-1">
                {getStatusBadge(workspace.subscription?.status)}
              </div>
            </div>
            <div>
              <p className="text-sm text-neutral-500">Period Start</p>
              <p className="text-lg font-medium">
                {workspace.subscription?.currentPeriodStart
                  ? formatDate(workspace.subscription.currentPeriodStart)
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-500">Period End</p>
              <p className="text-lg font-medium">
                {workspace.subscription?.currentPeriodEnd
                  ? formatDate(workspace.subscription.currentPeriodEnd)
                  : '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Force Set Subscription */}
      <Card>
        <CardHeader>
          <CardTitle>Force Set Subscription</CardTitle>
          <CardDescription>
            Override subscription settings for this workspace. Use with caution.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={planCode} onValueChange={setPlanCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  {DEV_PLANS.map((plan) => (
                    <SelectItem key={plan.code} value={plan.code}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <DateInput value={startDate} onChange={setStartDate} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <DateInput value={endDate} onChange={setEndDate} />
            </div>
          </div>

          <Dialog>
            <DialogTrigger asChild>
              <Button disabled={!planCode || !startDate || !endDate}>
                <Save className="mr-2 h-4 w-4" />
                Apply Changes
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Subscription Change</DialogTitle>
                <DialogDescription>
                  This will override the subscription for workspace "{workspace.workspaceName}".
                  Type the workspace name to confirm.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Input
                  placeholder={workspace.workspaceName}
                  value={confirmWorkspaceName}
                  onChange={(e) => setConfirmWorkspaceName(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmWorkspaceName('')}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={confirmWorkspaceName !== workspace.workspaceName}
                  onClick={() => {
                    setSubscriptionMutation.mutate()
                    setConfirmWorkspaceName('')
                  }}
                >
                  Confirm
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Locked Resources */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Locked Resources</span>
            {workspace.lockedResources.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => unlockAllMutation.mutate()}
                disabled={unlockAllMutation.isPending}
              >
                <Unlock className="mr-2 h-4 w-4" />
                Unlock All
              </Button>
            )}
          </CardTitle>
          <CardDescription>
            Resources locked due to plan downgrade
          </CardDescription>
        </CardHeader>
        <CardContent>
          {workspace.lockedResources.length === 0 ? (
            <p className="text-sm text-neutral-500">No locked resources</p>
          ) : (
            <div className="space-y-2">
              {workspace.lockedResources.map((resource, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between rounded-lg border border-neutral-200 p-3"
                >
                  <div>
                    <p className="font-medium capitalize">{resource.resourceType}</p>
                    <p className="text-xs text-neutral-500">{resource.resourceId}</p>
                  </div>
                  <Badge variant="warning">
                    <Lock className="mr-1 h-3 w-3" />
                    Locked
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}